import { useCallback, useEffect, useRef, useState } from 'react'

/** Does this browser expose a camera we can drive ourselves?
 *
 *  getUserMedia only exists in a secure context — HTTPS or localhost. On a
 *  phone hitting the site over plain http this is undefined, which is a common
 *  and confusing way for "the camera button does nothing" to happen. We check
 *  up front so we can offer the native camera app instead of failing silently.
 */
export const cameraSupported = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  window.isSecureContext

type Stage = 'live' | 'review'

/** Full-screen camera for photographing a paper invoice.
 *
 *  Deliberately two-stage: shoot, then look at what you got and decide. A blurry
 *  or half-cropped invoice costs a model call and thirty seconds of waiting
 *  before anyone finds out, so the confirmation step pays for itself.
 */
export default function CameraCapture({ onCapture, onClose }: {
  onCapture: (file: File) => void
  onClose: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [stage, setStage] = useState<Stage>('live')
  const [shot, setShot] = useState<{ url: string; file: File } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }, [])

  useEffect(() => {
    let cancelled = false
    navigator.mediaDevices
      .getUserMedia({
        // The rear camera is the one pointed at the paper. `ideal` rather than
        // `exact` so a laptop with only a front camera still works.
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 2048 },
          height: { ideal: 1536 },
        },
        audio: false,
      })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return }
        streamRef.current = stream
        if (videoRef.current) videoRef.current.srcObject = stream
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const name = e instanceof DOMException ? e.name : ''
        setError(
          name === 'NotAllowedError'
            ? 'Camera permission was refused. Allow it in your browser settings, or choose a file instead.'
            : name === 'NotFoundError'
              ? 'No camera found on this device.'
              : 'Could not start the camera.',
        )
      })
    return () => { cancelled = true; stop() }
  }, [stop])

  // Going back to live from the review shot remounts the <video>, which comes
  // back with no srcObject — the stream is still running, but nothing is
  // showing it. Re-attach whenever we return to the live stage, or Retake
  // hands you a blank camera.
  useEffect(() => {
    if (stage === 'live' && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current
    }
  }, [stage])

  // Escape closes, matching every other overlay people have used.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => () => { if (shot) URL.revokeObjectURL(shot.url) }, [shot])

  const shoot = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')?.drawImage(video, 0, 0)
    canvas.toBlob((blob) => {
      if (!blob) { setError('Could not capture the photo.'); return }
      // Named like a file because it becomes one — the rest of the pipeline
      // cannot tell a photographed invoice from an uploaded one, and shouldn't.
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
      const file = new File([blob], `invoice-photo-${stamp}.jpg`, { type: 'image/jpeg' })
      setShot({ url: URL.createObjectURL(blob), file })
      setStage('review')
    }, 'image/jpeg', 0.92)
  }

  const retake = () => {
    if (shot) URL.revokeObjectURL(shot.url)
    setShot(null)
    setStage('live')
  }

  const use = () => {
    if (!shot) return
    stop()
    onCapture(shot.file)
    onClose()
  }

  return (
    <div className="cam" role="dialog" aria-modal="true" aria-label="Take a photo of an invoice">
      <div className="cam-bar">
        <span className="cam-title">
          {stage === 'live' ? 'Photograph the invoice' : 'Is this readable?'}
        </span>
        <div className="grow" />
        <button className="btn btn-sm btn-ghost cam-x" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div className="cam-stage">
        {error ? (
          <div className="cam-error">
            <div className="cam-error-text">{error}</div>
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        ) : stage === 'live' ? (
          <>
            <video ref={videoRef} className="cam-video" autoPlay playsInline muted />
            {/* A frame to aim with. Invoices photographed edge-to-edge lose
                their totals to the crop more often than anything else. */}
            <div className="cam-guide" aria-hidden="true" />
          </>
        ) : (
          <img className="cam-video" src={shot?.url} alt="The photo you just took" />
        )}
      </div>

      <div className="cam-foot">
        {stage === 'live' ? (
          <>
            <div className="cam-hint">
              Fill the frame with the invoice. Flat, good light, no shadow across the totals.
            </div>
            <button className="cam-shutter" onClick={shoot} disabled={!!error}
              aria-label="Take photo" />
          </>
        ) : (
          <>
            <div className="cam-hint">
              Check the invoice number, the totals and the GSTIN are all legible.
            </div>
            <div className="cam-actions">
              <button className="btn" onClick={retake}>Retake</button>
              <button className="btn btn-primary" onClick={use}>Use this photo</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
